import React, { JSX, useContext, useEffect, useRef, useState } from "react"
import { cloneDeep, isEqual, Many, orderBy, sum } from "lodash";
import { BenchmarkingContext } from "@app/overviews/network-benchmarking-dashboard/";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters";
import { MedicareFiltersContext, MedicareParentOrganizationSortingTypeEnum, MedicareNetworkFiltersTable, MedicareNetworkPill } from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import { MedicareNetwork, MedicareParentOrganization } from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import { LoadingSpinner } from "@app/shared";
import { QESButton, QESSelect } from "@app/shared/qes-standards";
import { printNumberAsString } from "@app/shared/waffles";

const MedicareNetworkFilters = (): JSX.Element => {
  const [defaultPillDisplayCount, setDefaultPillDisplayCount] = useState<number>(null);
  const [showingMore, setShowingMore] = useState<boolean>(false);
  const [sortedParentOrganizations, setSortedParentOrganizations] = useState<MedicareParentOrganization[]>([]);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const benchmarkingMedicareFiltersContext = useContext(MedicareFiltersContext);
  const benchmarkingContext = useContext(BenchmarkingContext);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!!benchmarkingMedicareFiltersContext.selectedSortType) {
      benchmarkingMedicareFiltersContext.setSelectedSortType(null);
    }
  }, [benchmarkingMedicareFiltersContext.networkFiltersLoading]);

  useEffect(() => {
    if (!!benchmarkingMedicareFiltersContext.parentOrganizations?.length && ref?.current && !benchmarkingMedicareFiltersContext.selectedSortType) {
      benchmarkingMedicareFiltersContext.setSelectedSortType(MedicareParentOrganizationSortingTypeEnum.MembershipDesc);
    }
  }, [benchmarkingMedicareFiltersContext.parentOrganizations, ref?.current]);

  useEffect(() => {
    if (!!benchmarkingMedicareFiltersContext.selectedSortType && !!ref?.current) {
      // "your qes networks" pill should be excluded from sorting and should always maintain the first position
      const yourQesNetworks = cloneDeep(benchmarkingMedicareFiltersContext.parentOrganizations.find(x => x.parentOrganizationName === "Your QES Networks"));
      let newSortedBenchmarkingParentOrganizations = cloneDeep(benchmarkingMedicareFiltersContext.parentOrganizations.filter(x => x.parentOrganizationName !== "Your QES Networks"));

      switch (benchmarkingMedicareFiltersContext.selectedSortType) {
        case MedicareParentOrganizationSortingTypeEnum.AlphabeticalAsc:
          newSortedBenchmarkingParentOrganizations = sortByName(yourQesNetworks, newSortedBenchmarkingParentOrganizations, "asc");
          break;
        case MedicareParentOrganizationSortingTypeEnum.AlphabeticalDesc:
          newSortedBenchmarkingParentOrganizations = sortByName(yourQesNetworks, newSortedBenchmarkingParentOrganizations, "desc");
          break;
        case MedicareParentOrganizationSortingTypeEnum.MembershipAsc:
          newSortedBenchmarkingParentOrganizations = sortByMembership(yourQesNetworks, newSortedBenchmarkingParentOrganizations, "asc");
          break;
        case MedicareParentOrganizationSortingTypeEnum.MembershipDesc:
          newSortedBenchmarkingParentOrganizations = sortByMembership(yourQesNetworks, newSortedBenchmarkingParentOrganizations, "desc");
          break;
        default:
          break;
      }

      let parentOrgs = [yourQesNetworks];
      parentOrgs = parentOrgs.concat(newSortedBenchmarkingParentOrganizations);

      //update the selectedParentOrganizations with the sorted items so the UI gets updated
      benchmarkingMedicareFiltersContext.selectedParentOrganizations.forEach((p, index:number) => {
        const selectedParentIndex = parentOrgs.findIndex(o => o.parentOrganizationName === p.parentOrganizationName);
        if (selectedParentIndex > -1) {
          benchmarkingMedicareFiltersContext.selectedParentOrganizations.splice(index, 1, parentOrgs[selectedParentIndex]);
        }
      });

      if (!showingMore) {
        // the purpose of these calculations is to display as many pills as possible within two rows
        // while taking into account varying screen sizes and pill sizes
        // and also keeping the "show more" button in line with the pills (otherwise, container height toggling would've worked just fine)
        const getTextWidth = (text: string) => {
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          context.font = getComputedStyle(document.body)?.font;

          return context.measureText(text)?.width;
        }

        // row one's maximum width consists of the available space, minus padding,
        const row1Width = ref.current.clientWidth - 20;
        // row two's maximum width consists of the available space, minus padding,
        // minus the width for the load more button that we want to display in line
        const loadMoreWidth = getTextWidth("Load More") + 48;
        const row2Width = ref.current.clientWidth - 20 - loadMoreWidth;
        let rowOneSize = 0;
        let rowTwoSize = 0;
        let rowOnePillCount = 0;
        let rowTwoPillCount = 0;

        for (let i = 0; i < parentOrgs.length; i++) {
          if (!rowOnePillCount || !rowTwoPillCount) {
            // pill width consists of the larger of the two text widths (name or membership, capped at 196) plus the normal base size of a pill
            const nameWidth = getTextWidth(parentOrgs[i].parentOrganizationName);
            const membershipWidth = getTextWidth(`Membership: ${printNumberAsString(sum(parentOrgs[i].hContracts.map(x => x.membership)), "---")}`);
            const largestTextWidth = nameWidth > membershipWidth ? nameWidth : membershipWidth;
            const pillWidth = (largestTextWidth < 196 ? largestTextWidth : 196) + 71;

            if (rowOneSize + pillWidth < row1Width) {
              rowOneSize = rowOneSize + pillWidth;
            } else {
              if (!rowOnePillCount) {
                rowOnePillCount = i;
              }

              if (rowTwoSize + pillWidth < row2Width) {
                rowTwoSize = rowTwoSize + pillWidth;
              } else {
                if (!rowTwoPillCount) {
                  rowTwoPillCount = i - rowOnePillCount;
                }
              }
            }
          }
        }

        if (!!rowOnePillCount && !!rowTwoPillCount) {
          // only assign default pill display count if both rows will be full
          setDefaultPillDisplayCount(rowOnePillCount + rowTwoPillCount);
        }
      }

      setSortedParentOrganizations(parentOrgs);
    }
  }, [benchmarkingMedicareFiltersContext.selectedSortType, ref?.current]);

  const sortByMembership = (qesNetwork: MedicareParentOrganization, benchmarkingParentOrganizations: MedicareParentOrganization[], sortDirection: Many<boolean | "asc" | "desc">): MedicareParentOrganization[] => {
    benchmarkingParentOrganizations = orderBy(benchmarkingParentOrganizations, (p: MedicareParentOrganization) => sum(p.hContracts.map(x => x.membership)), sortDirection);
    benchmarkingParentOrganizations.forEach((p) => {
      const sortedHContracts = orderBy(p.hContracts, (h: MedicareNetwork) => h.membership, sortDirection);
      p.hContracts = sortedHContracts;
    });

    qesNetwork.hContracts = orderBy(qesNetwork.hContracts, (h: MedicareNetwork) => h.membership, sortDirection);
    return benchmarkingParentOrganizations;
  }

  const sortByName = (qesNetwork: MedicareParentOrganization, benchmarkingParentOrganizations: MedicareParentOrganization[], sortDirection: Many<boolean | "asc" | "desc">): MedicareParentOrganization[] => {
    benchmarkingParentOrganizations = orderBy(benchmarkingParentOrganizations, (p) => p.parentOrganizationName, sortDirection);
    benchmarkingParentOrganizations.forEach((p) => {
      const sortedHContracts = orderBy(p.hContracts, (h) => h.contractId, sortDirection);
      p.hContracts = sortedHContracts;
    });

    qesNetwork.hContracts = orderBy(qesNetwork.hContracts, (h) => h.contractId, sortDirection);
    return benchmarkingParentOrganizations;
  }

  const onParentOrganizationToggle = (parentOrganization: MedicareParentOrganization): void => {
    const newParentOrganizations: MedicareParentOrganization[] = cloneDeep(benchmarkingMedicareFiltersContext.selectedParentOrganizations);

    if (newParentOrganizations.map(x => x.parentOrganizationName).includes(parentOrganization.parentOrganizationName)) {
      const selectedHContracts: MedicareNetwork[] = cloneDeep(benchmarkingFiltersContext.selectedNetworks as MedicareNetwork[]);

      // when deselecting a parent organization, also make sure to deselect any h contracts that correspond to that parent organization
      const parentOrganizationContractIds = parentOrganization.hContracts.map(x => x.contractId);
      const selectedContractIds = benchmarkingFiltersContext.selectedNetworks.map(x => x.contractId);

      if (selectedContractIds.some(x => parentOrganizationContractIds.includes(x))) {
        benchmarkingFiltersContext.setSelectedNetworks(selectedHContracts.filter(x => !parentOrganizationContractIds.includes(x.contractId)));
      }

      benchmarkingMedicareFiltersContext.setSelectedParentOrganizations(newParentOrganizations.filter(x => x.parentOrganizationName !== parentOrganization.parentOrganizationName));
    } else {
      // ensure that if "your qes networks" is selected, it is always in the first position
      if (parentOrganization.parentOrganizationName === "Your QES Networks") {
        benchmarkingMedicareFiltersContext.setSelectedParentOrganizations([parentOrganization].concat(newParentOrganizations));
      } else {
        benchmarkingMedicareFiltersContext.setSelectedParentOrganizations(newParentOrganizations.concat(parentOrganization));
      }
    }
  }

  const clearDisabled = (): boolean => {
    if (!benchmarkingMedicareFiltersContext.networkFiltersLoading && (
      !!benchmarkingMedicareFiltersContext.selectedParentOrganizations.length || !!benchmarkingFiltersContext.selectedNetworks.length
    )) {
      return false;
    }

    return true;
  }

  const submitDisabled = (): boolean => {
    const hContractsEqual = isEqual(
      benchmarkingFiltersContext.displayedNetworkFilters.sort((a, b) => a.contractId.localeCompare(b.contractId)),
      benchmarkingFiltersContext.selectedNetworks.sort((a, b) => a.contractId.localeCompare(b.contractId))
    );

    if (!benchmarkingMedicareFiltersContext.networkFiltersLoading &&
      benchmarkingFiltersContext.selectedNetworks.length > 1 &&
      benchmarkingFiltersContext.selectedNetworks.length < 6 &&
      !hContractsEqual
    ) {
      return false;
    }

    return true;
  }

  return (
    <React.Fragment>
      {!benchmarkingMedicareFiltersContext.networkFiltersLoading ? (
        <React.Fragment>
          <div id="benchmarking-dashboard__network-filters--container">
            <hr className="w-100" />
            <div className="row w-100">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-end">
                  <div>
                    <label className="label-m mb0 d-block">
                      Comparison Networks
                    </label>

                    <label className={`copy-text-default d-block mb-3 ${!benchmarkingFiltersContext.displayedPrimaryFilters && "hidden-instructions"}`}>
                      Please select at least 2 and up to 5 networks for comparison.
                    </label>
                  </div>

                  <div>
                    <QESSelect
                      containerClassName="mb-3"
                      testId="network-benchmarking-dashboard__network-filters--parent-organization-sort-by"
                      label="Sort By"
                      onChange={(e) => benchmarkingMedicareFiltersContext.setSelectedSortType(e.target.value as MedicareParentOrganizationSortingTypeEnum)}
                      value={benchmarkingMedicareFiltersContext.selectedSortType || ""}
                    >
                      {Object.values(MedicareParentOrganizationSortingTypeEnum).map((sortingType, index) => (
                        <option
                          key={index}
                          value={sortingType}
                        >
                          {sortingType}
                        </option>
                      ))}
                    </QESSelect>
                  </div>
                </div>
              </div>
            </div>

            <div className="row w-100">
              <div className="col-12 d-flex flex-wrap align-items-end" ref={ref}>
                {((!showingMore && !!defaultPillDisplayCount) ? sortedParentOrganizations.slice(0, defaultPillDisplayCount) : sortedParentOrganizations).map((parentOrganization, index) => (
                  <MedicareNetworkPill
                    key={index}
                    onParentOrganizationClick={onParentOrganizationToggle}
                    parentOrganization={parentOrganization}
                    selected={benchmarkingMedicareFiltersContext.selectedParentOrganizations.map(x => x.parentOrganizationName).includes(parentOrganization.parentOrganizationName)}
                  />
                ))}

                {(!!defaultPillDisplayCount && sortedParentOrganizations.length > defaultPillDisplayCount) && (
                  <QESButton
                    qesTestId="network-benchmarking-dashboard__network-filters--show-more-less"
                    qesType="secondary"
                    onClick={() => setShowingMore(!showingMore)}
                    className="mr-3"
                  >
                    Show {showingMore ? "Less" : "More"}
                  </QESButton>
                )}
              </div>
            </div>

            <div className="row w-100 mt-3" id="benchmarking-dashboard__network-filters--networks">
              {!!benchmarkingMedicareFiltersContext.selectedParentOrganizations.length && (
                benchmarkingMedicareFiltersContext.selectedParentOrganizations.map((parentOrganization, index) => (
                  <div className="col-md-4 w-100 benchmarking-network" key={index}>
                    <label className="label-s mb-0 mt-1">
                      {parentOrganization.parentOrganizationName}
                    </label>

                    <MedicareNetworkFiltersTable
                      data={parentOrganization.hContracts}
                      parentOrganizationName={parentOrganization.parentOrganizationName.replace(/\s+/g, '-').toLowerCase()}
                    />
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-end mt-3">
                <QESButton
                  disabled={clearDisabled()}
                  qesTestId="network-benchmarking-dashboard__network-filters--clear"
                  qesType="secondary-danger"
                  onClick={() => benchmarkingMedicareFiltersContext.onClearNetworkFiltersClick()}
                  className="mr-3"
                >
                  Clear
                </QESButton>

                <QESButton
                  disabled={submitDisabled()}
                  qesTestId="network-benchmarking-dashboard__network-filters--submit"
                  qesType="primary"
                  onClick={() => benchmarkingContext.onSubmitNetworkFiltersClick(benchmarkingMedicareFiltersContext.parentOrganizations)}
                >
                  Submit
                </QESButton>
              </div>
            </div>
          </div>
        </React.Fragment>
      ) : (
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-center mt-3">
              <LoadingSpinner />
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

export default MedicareNetworkFilters;
